import { useApiMutation, type ApiClient } from "@gladis/api-client";
import { formatEur } from "@gladis/shared";
import { Button, Dialog, Label, MoneyInput, TextField, parseEurInput } from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";
import { useState, type FormEvent, type ReactNode } from "react";

import { VEHICLES_KEY } from "../hooks.js";

interface StillLinked {
  id: string;
  description: string;
  monthlyEquivalentInEuros: number;
}

/**
 * Record that a vehicle was sold.
 *
 * ==========================================================================================
 * SELLING IS NOT ARCHIVING, AND THE TWO ARE NEVER ONE CONTROL
 * ==========================================================================================
 *
 * Archiving withdraws a record that should not have existed — the vehicle leaves net worth
 * INCLUDING its past, and it comes back. Selling records something that happened: the vehicle
 * contributes right up to the sale date and its history stays true.
 *
 * Income draws the identical line between ending and archiving, and records the consequence:
 * collapsing them empties a past year. A member who sold a car and archived it instead loses every
 * month it was on the balance sheet, silently.
 *
 * ==========================================================================================
 * THE STILL-LINKED COSTS ARE ADVICE, AND ARRIVE WITH A SUCCESSFUL SALE
 * ==========================================================================================
 *
 * The server returns 200 and names the costs that were still attached (FR-049). They are NOT
 * archived automatically: a household that sold a car on the 3rd may still be paying its insurance
 * until the policy is cancelled, and ending them here would delete real recurring costs the
 * household is still paying.
 *
 * So the dialog shows them and stays open. The member decides.
 *
 * MOUNTED ALWAYS, RESET ON OPEN — not "mounted only while open", which the plan asked for on 016's
 * precedent. A native `<dialog>` cannot be conditionally rendered: `Dialog` calls `showModal()` from
 * an effect on an element it already holds a ref to, and an element appearing in the same commit as
 * `open` has no first render in which to be closed. `RecordBalanceForm` records the same correction.
 */
export function DisposalDialog({
  open,
  onClose,
  vehicleId,
  vehicleName,
  lastValuationInEuros,
  onDisposed,
}: {
  open: boolean;
  onClose: () => void;
  vehicleId: string;
  vehicleName: string;
  /**
   * What the household last believed it was worth, so the two sit side by side.
   *
   * Null when it was never valued — they sold something they had not estimated, and there is simply
   * nothing to compare against. The dialog says nothing rather than showing a zero.
   */
  lastValuationInEuros: number | null;
  onDisposed?: () => void;
}): ReactNode {
  const t = useT();
  const today = () => new Date().toISOString().slice(0, 10);

  const [price, setPrice] = useState("");
  const [on, setOn] = useState(today);
  const [stillLinked, setStillLinked] = useState<StillLinked[]>([]);
  const [target, setTarget] = useState({ id: vehicleId, name: vehicleName });

  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setTarget({ id: vehicleId, name: vehicleName });
      setPrice("");
      setOn(today());
      setStillLinked([]);
    }
  }

  const dispose = useApiMutation<
    { stillLinked: StillLinked[] },
    { on: string; priceInEuros: number }
  >(
    (client: ApiClient, input) =>
      client.post<{ stillLinked: StillLinked[] }>(`/vehicles/${target.id}/dispose`, input),
    // A sale removes the vehicle from net worth, so the dashboard's cache is stale too.
    { invalidates: [VEHICLES_KEY, ["dashboard"], ["expenses"]] },
  );

  function submit(event: FormEvent): void {
    event.preventDefault();
    const euros = parseEurInput(price);
    if (euros === null) return;

    dispose.mutate(
      { on, priceInEuros: euros },
      {
        onSuccess: (result) => {
          onDisposed?.();
          /*
           * CLOSE ONLY WHEN THERE IS NOTHING LEFT TO SAY.
           *
           * With costs still attached the dialog stays open showing them, because the sale succeeded
           * and the member now has a decision to make. Closing would report the advice by not
           * showing it.
           */
          if (result.stillLinked.length === 0) onClose();
          else setStillLinked(result.stillLinked);
        },
      },
    );
  }

  const sold = stillLinked.length > 0;

  return (
    <Dialog open={open} onClose={onClose} title={t("veh.dispose.heading", { name: target.name })}>
      {sold ? (
        <div className="flex flex-col gap-4">
          <p className="text-sm">{t("veh.dispose.stillLinked", { count: stillLinked.length })}</p>
          <ul className="flex flex-col gap-1 text-sm">
            {stillLinked.map((cost) => (
              <li key={cost.id} className="flex justify-between gap-4">
                <span>{cost.description}</span>
                <span className="text-muted">{formatEur(cost.monthlyEquivalentInEuros)}</span>
              </li>
            ))}
          </ul>
          <div className="flex justify-end">
            <Button type="button" onClick={onClose}>
              {t("common.done")}
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div>
            <Label htmlFor="disposal-price">{t("veh.dispose.price")}</Label>
            <MoneyInput id="disposal-price" value={price} onValueChange={setPrice} required />
            {lastValuationInEuros === null ? null : (
              // The comparison FR-046 exists for, offered before the sale rather than after it.
              <p className="mt-1 text-xs text-muted">
                {t("veh.dispose.lastValuation", {
                  amount: formatEur(lastValuationInEuros),
                })}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="disposal-date">{t("veh.dispose.date")}</Label>
            <TextField
              id="disposal-date"
              type="date"
              value={on}
              max={today()}
              onChange={(event) => {
                setOn(event.target.value);
              }}
              required
            />
          </div>

          {dispose.error ? (
            <p className="text-sm text-danger">{errorSentence(dispose.error)}</p>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" pending={dispose.isPending}>
              {t("veh.dispose.submit")}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
